import { eventBus } from '../../core/events/index.js';
import { HangoutState, HANGOUT_EVENTS } from './state/HangoutState.js';
import { updateCharacterImage, fetchCharacterSprite } from './ui/character-manager.js';
import { timeProgressionService } from './services/TimeProgressionService.js';
import { backgroundRenderer } from '../../core/services/BackgroundRenderer.js';

export async function initHangout(options = {}) {
  const { storageManager } = await import('../../core/storage/index.js');
  const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
  
  // Initialize and start TimeProgressionService
  await timeProgressionService._checkAndRestoreState();
  
  if (!options.element) {
    throw new Error('Hangout feature requires an element to render into');
  }

  const eventsInstance = options.events || eventBus;
  const { characterId, scenarioId, outfitId, startupPrefetch = null } = options;

  // Only skip initialization if we have an existing conversation that's not new
  const shouldSkipInit = hangoutConfig && (
    (!hangoutConfig.isNewConversation && hangoutConfig.conversationId) ||
    (hangoutConfig.allCharacters?.length > 0 && !hangoutConfig.isNewConversation)
  );

  if (shouldSkipInit) {
    return { destroy: () => {} };
  }

  const hangoutState = HangoutState.getInstance();
  const element = options.element;
  
  const characterImage = element.querySelector('.character-image img');
  const chatContainer = element.querySelector('.chat-container');
  const chatInput = element.querySelector('.chat-input-container');

  if (!characterImage || !chatContainer || !chatInput) {
    throw new Error('Required UI elements not found');
  }

  const unsubscribeHandlers = [];

  const safeSubscribe = (event, handler) => {
    unsubscribeHandlers.push(hangoutState.subscribe(event, handler));
  };

  safeSubscribe(HANGOUT_EVENTS.CHARACTER_SELECTED, async (character) => {
    if (!character) {
      console.error('No character selected');
      return;
    }

    try {
      const effectiveOutfitId = hangoutState.getSelectedOutfit() || outfitId;
      const spriteState = character.sprite_state ?? null;

      const { parseSpriteState } = await import('../../core/utils/hangoutUtils.js');
      const parsedState = parseSpriteState(spriteState);

      const spriteUrl = await fetchCharacterSprite(
        character.id,
        parsedState.action,
        effectiveOutfitId,
        0,
        true
      );
      
      updateCharacterImage(spriteUrl, character.name, 0, String(parsedState.action));
    } catch (error) {
      console.error('Failed to load character sprite:', error);
    }
  });

  const updateBackground = async (backgroundData) => {
    return backgroundRenderer.updateBackground(backgroundData);
  };

  safeSubscribe(HANGOUT_EVENTS.SPOT_SELECTED, async (spot) => {
    try {
      if ((Number(spot.background) || !spot.background) && spot.id) {
        await hangoutState.switchLocation({
          id: spot.id,
          name: spot.name || `Location ${spot.id}`,
          background: spot.background || null,
          timeOfDay: hangoutState.getCurrentTimeSlot()
        });
      }

      if (spot.background && !Number(spot.background)) {
        backgroundRenderer.applyBackgroundImage(spot.background);
      }
    } catch (error) {
      console.error('Failed to handle spot selection:', error);
      hangoutState.setError(error);
    }
  });

  safeSubscribe(HANGOUT_EVENTS.BACKGROUND_CHANGED, updateBackground);
  safeSubscribe(HANGOUT_EVENTS.TIME_OF_DAY_CHANGED, updateBackground);

  if (characterId && scenarioId) {
    
    // Start session - this handles all the initial data loading
    try {
      const session = {
        characterId,
        scenarioId,
        outfitId: outfitId || '0',
        conversationId: null,
        startupPrefetch
      };
      if (startupPrefetch) {
        hangoutState.setStartupPrefetch(startupPrefetch);
      }
      await hangoutState.startSession(session);

      // Apply initial background immediately
      try {
        await updateBackground();
      } catch (e) {
        console.warn('[initHangout] Initial background update failed:', e);
      }
    } catch (error) {
      console.error('Failed to start session:', error);
    }
  }

  return {
    destroy() {
      unsubscribeHandlers.forEach(unsubscribe => unsubscribe());
    }
  };
}
